import React, {useCallback, useContext, useMemo, useState} from "react";
import {columns} from "./columns.jsx";
import {ConfigProvider, Switch} from "antd";
import {keyBy} from "lodash";
import {
    deliveriesPageSize,
    DeliveriesShortcuts,
    DeliveryProviderDeliveryStatus,
    DeliveryShortcutsKeys
} from "./consts.jsx";
import "./delivery-provider-deliveries.css";
import {TableDateRangeTable} from "../../../components/OrdersDateRangeTable/TableDateRangeTable.jsx";
import {StringBuilder} from "../../../AppUtils.js";
import {OrderChangesHistoryModal} from "../../../admin/AdminOrders/OrderChangesHistoryModal.jsx";
import {DeliveryProviderDeliveriesContext} from "./DeliveryProviderDeliveriesContext.jsx";
import {DeliveryProviderDeliveriesExternalDriverModal} from "./DeliveryProviderDeliveriesExternalDriverModal";

export const DeliveryProviderDeliveries = ({history}) => {
    const [visibleHistoryModal, setOrderHistoryModal] = useState(null);
    const [searchInput, setSearchInput] = useState("");

    const {
        deliveries,
        totalDeliveriesCount,
        isLoading,
        drivers,
        currentPage,
        setCurrentPage,
        onChangeDeliveryDriver,
        onMarkAsDelivered,
        onRevertDeliveredStatus,
        showDeliveredDeliveries,
        setShowDeliveredDeliveries,
        openExternalDriverModal,
        closeExternalDriverModal,
        selectedDeliveryId,
        selectedDriverId
    } = useContext(DeliveryProviderDeliveriesContext);

    const filteredDeliveries = useMemo(
        () =>
            deliveries?.filter(({companyName, driverId, proposalId, price, order, destinationAddress}) =>
                [
                    companyName,
                    driverId,
                    String(proposalId),
                    String(price),
                    order.providerBrand,
                    destinationAddress?.city,
                    destinationAddress?.address
                ].some(property => property?.toLowerCase()?.includes(searchInput?.toLowerCase()))
            ),
        [searchInput, deliveries]
    );

    const handleShowDeliveredDeliveriesToggle = useCallback(
        checked => {
            setShowDeliveredDeliveries(prev => !prev);
        },
        [setShowDeliveredDeliveries]
    );

    const driverIdToDriver = useMemo(() => keyBy(drivers, driver => driver?.driverId), [drivers]);

    const onChangeDriver = useCallback(
        async (deliveryId, driverId) => {
            const driver = driverIdToDriver[driverId];

            if (driver?.isExternal) {
                openExternalDriverModal(deliveryId, driverId);

                return;
            }

            await onChangeDeliveryDriver(deliveryId, driverId, false);
        },
        [driverIdToDriver, onChangeDeliveryDriver]
    );

    const handleExternalDriverSave = useCallback(
        async ({name, phone}) => {
            await onChangeDeliveryDriver(selectedDeliveryId, selectedDriverId, false, {name, phone});
            closeExternalDriverModal();
        },
        [selectedDeliveryId, selectedDriverId, onChangeDeliveryDriver]
    );

    return (
        <div className="delivery-provider-deliveries-page">
            <ConfigProvider direction="rtl">
                <TableDateRangeTable
                    title={new StringBuilder("כמות משלוחים", " - ").append(`${totalDeliveriesCount ?? 0}`).toString()}
                    shortcutSearchOptions={DeliveriesShortcuts}
                    defaultShortcutSearchOptions={DeliveryShortcutsKeys.todayDeliveries}
                    allowDeselectShortcutSearchOptions={false}
                    tableClassName="delivery-provider-deliveries-table"
                    onInputSearch={value => setSearchInput(value)}
                    extraContent={
                        <div className="driver-deliveries-show-delivered-deliveries-toggle-container">
                            <Switch
                                onChange={handleShowDeliveredDeliveriesToggle}
                                checked={!showDeliveredDeliveries}
                                title={!showDeliveredDeliveries ? "משלוחים פתוחים" : "משלוחים סגורים"}
                            />
                        </div>
                    }
                    pagination={{
                        position: ["bottomRight"],
                        showSizeChanger: false,
                        current: currentPage,
                        defaultPageSize: deliveriesPageSize,
                        total: totalDeliveriesCount,
                        showTotal: total => <span>סה״כ {total}</span>,
                        onChange: (page, pageSize) => setCurrentPage(page)
                    }}
                    isLoadingOrderTable={isLoading}
                    data={filteredDeliveries ?? []}
                    showSearchByCreatedAt={false}
                    columns={columns(
                        currentPage,
                        [
                            {
                                title: "צפייה במשלוח",
                                onClick: ({deliveryId}) => history?.push(`/delivery/${deliveryId}`)
                            },
                            {
                                title: "היסטוריית משלוח",
                                onClick: ({orderId}) => setOrderHistoryModal(orderId)
                            },
                            {
                                shouldShow: delivery => delivery.status !== DeliveryProviderDeliveryStatus.supplied,
                                title: "סמן כסופק",
                                onClick: ({deliveryId}) => onMarkAsDelivered({deliveryId})
                            },
                            {
                                shouldShow: delivery => delivery.status === DeliveryProviderDeliveryStatus.supplied,
                                title: "סמן כלא סופק",
                                onClick: ({deliveryId}) => onRevertDeliveredStatus({deliveryId})
                            }
                        ],
                        drivers,
                        onChangeDriver
                    )}
                    disableFutureDates={false}
                />

                <OrderChangesHistoryModal
                    visible={!!visibleHistoryModal}
                    orderId={visibleHistoryModal}
                    onClose={() => setOrderHistoryModal(null)}
                />

                <DeliveryProviderDeliveriesExternalDriverModal onSave={handleExternalDriverSave} />
            </ConfigProvider>
        </div>
    );
};
